import { useState, useMemo } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Download, 
  Eye, 
  Star, 
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  UserPlus,
  Search,
  FileText
} from 'lucide-react';
import type { BulkAnalysisResult, BulkAnalysisSummary } from './types/bulk';
import { cn } from '@/lib/utils';

interface BulkAnalysisResultsProps {
  results: BulkAnalysisResult[];
  onCandidateSelect?: (candidate: BulkAnalysisResult) => void;
  onAddToProject?: (candidate: BulkAnalysisResult) => void;
  onExport?: (format: 'csv' | 'pdf' | 'excel') => void;
  projectId?: string;
}

export function BulkAnalysisResults({
  results,
  onCandidateSelect,
  onAddToProject,
  onExport,
  projectId
}: BulkAnalysisResultsProps) {

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'score' | 'name' | 'date'>('score');
  const [filterBy, setFilterBy] = useState<'all' | 'strong_fit' | 'potential_fit' | 'needs_consideration' | 'not_recommended'>('all');

  // Calculate summary statistics
  const summary: BulkAnalysisSummary = useMemo(() => {
    const successful = results.filter(r => !r.error);
    const failed = results.filter(r => r.error);
    
    const averageScore = successful.length > 0 
      ? successful.reduce((sum, r) => sum + r.overallScore, 0) / successful.length
      : 0;

    // Get top candidates (top 20% or minimum 3)
    const topCount = Math.max(3, Math.ceil(successful.length * 0.2));
    const topCandidates = successful
      .sort((a, b) => b.overallScore - a.overallScore)
      .slice(0, topCount);

    // Analyze common skills
    const skillsMap = new Map<string, number>();
    successful.forEach(candidate => {
      candidate.matchedSkills.forEach(skill => {
        skillsMap.set(skill, (skillsMap.get(skill) || 0) + 1);
      });
    });

    const commonSkills = Array.from(skillsMap.entries())
      .map(([skill, frequency]) => ({ skill, frequency }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 10);

    // Analyze skills gaps
    const gapsMap = new Map<string, number>();
    successful.forEach(candidate => {
      candidate.missingSkills.forEach(skill => {
        gapsMap.set(skill, (gapsMap.get(skill) || 0) + 1);
      });
    });

    const skillsGaps = Array.from(gapsMap.entries())
      .map(([skill, frequency]) => ({ skill, frequency }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 10);

    // Calculate total processing time
    const processingTime = results.reduce((sum, r) => sum + r.processingTime, 0);

    // Generate recommendations
    const recommendations: string[] = [];
    
    if (topCandidates.length > 0) {
      recommendations.push(`${topCandidates.length} candidates show strong potential with scores above ${Math.round(topCandidates[topCandidates.length - 1].overallScore)}`);
    }
    
    if (commonSkills.length > 0) {
      recommendations.push(`Most candidates have strong ${commonSkills[0].skill} skills (${commonSkills[0].frequency}/${successful.length} candidates)`);
    }
    
    if (skillsGaps.length > 0) {
      recommendations.push(`Consider training programs for ${skillsGaps[0].skill} - commonly missing skill`);
    }

    if (failed.length > 0) {
      recommendations.push(`${failed.length} analyses failed - review file formats and content quality`);
    }

    return {
      totalFiles: results.length,
      successfulAnalyses: successful.length,
      failedAnalyses: failed.length,
      averageScore,
      topCandidates,
      commonSkills,
      skillsGaps,
      processingTime,
      recommendations
    };
  }, [results]);

  // Filter and sort results
  const filteredResults = useMemo(() => {
    let filtered = results;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(candidate =>
        candidate.candidateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        candidate.matchedSkills.some(skill => 
          skill.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Apply verdict filter
    if (filterBy !== 'all') {
      filtered = filtered.filter(candidate => candidate.verdict === filterBy);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'score':
          return b.overallScore - a.overallScore;
        case 'name':
          return a.candidateName.localeCompare(b.candidateName);
        case 'date':
          return b.analysisDate.getTime() - a.analysisDate.getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [results, searchQuery, sortBy, filterBy]);

  // Get verdict color and icon
  const getVerdictDisplay = (verdict: BulkAnalysisResult['verdict']) => {
    switch (verdict) {
      case 'strong_fit':
        return {
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: <CheckCircle className="h-3 w-3" />,
          label: 'Strong Fit'
        };
      case 'potential_fit':
        return {
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          icon: <Star className="h-3 w-3" />,
          label: 'Potential Fit'
        };
      case 'needs_consideration':
        return {
          color: 'bg-amber-100 text-amber-800 border-amber-200',
          icon: <AlertTriangle className="h-3 w-3" />,
          label: 'Needs Review'
        };
      case 'not_recommended':
        return {
          color: 'bg-red-100 text-red-800 border-red-200',
          icon: <XCircle className="h-3 w-3" />,
          label: 'Not Recommended'
        };
      default:
        return {
          color: 'bg-slate-100 text-slate-800 border-slate-200',
          icon: <FileText className="h-3 w-3" />,
          label: 'Unknown'
        };
    }
  };

  // Export functionality
  const handleExport = (format: 'csv' | 'pdf' | 'excel') => {
    if (onExport) {
      onExport(format);
    }
  };

  if (results.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Users className="h-12 w-12 text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No Results Yet</h3>
          <p className="text-slate-500 text-center">
            Upload CVs and start the bulk analysis to see results here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-lg transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Total Candidates</p>
                <p className="text-2xl font-bold text-slate-900">{summary.totalFiles}</p>
              </div>
              <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-lg transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">Average Score</p>
                <p className="text-2xl font-bold text-slate-900">{summary.averageScore.toFixed(1)}</p>
              </div>
              <div className="h-12 w-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Bar */}
      <Card className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search candidates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl border-slate-300 focus:border-slate-500 focus:ring-slate-500"
                />
              </div>
              <Select value={filterBy} onValueChange={(value: any) => setFilterBy(value)}>
                <SelectTrigger className="w-48 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Candidates</SelectItem>
                  <SelectItem value="strong_fit">Strong Fit</SelectItem>
                  <SelectItem value="potential_fit">Potential Fit</SelectItem>
                  <SelectItem value="needs_consideration">Needs Review</SelectItem>
                  <SelectItem value="not_recommended">Not Recommended</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                <SelectTrigger className="w-40 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score">Sort by Score</SelectItem>
                  <SelectItem value="name">Sort by Name</SelectItem>
                  <SelectItem value="date">Sort by Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => handleExport('csv')} className="rounded-xl">
              <Download className="h-4 w-4 mr-2" />
              Export Results
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Grid */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-slate-900 tracking-tight">
            Analysis Results ({filteredResults.length})
          </h3>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredResults.map((candidate) => {
            const verdictDisplay = getVerdictDisplay(candidate.verdict);
            const getScoreColor = (score: number) => {
              if (score >= 8) return "text-green-600";
              if (score >= 6) return "text-blue-600";
              if (score >= 4) return "text-amber-600";
              return "text-red-600";
            };

            if (candidate.error) {
              return (
                <Card key={candidate.id} className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-red-200/60 hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <XCircle className="h-6 w-6 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-slate-900 truncate">{candidate.candidateName}</h4>
                            <p className="text-sm text-slate-600 truncate">{candidate.fileName}</p>
                          </div>
                          <Badge variant="destructive" className="ml-2">Error</Badge>
                        </div>
                        <p className="text-sm text-red-600 mt-2">{candidate.error}</p>
                        <div className="flex items-center gap-2 mt-4">
                          <span className="text-xs text-slate-500">
                            {candidate.analysisDate.toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Card key={candidate.id} className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Header: Name, Score, Verdict */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-slate-600">
                            {candidate.candidateName.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-slate-900 truncate">{candidate.candidateName}</h4>
                          <p className="text-sm text-slate-600 truncate">{candidate.fileName}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <span className={cn("text-2xl font-bold", getScoreColor(candidate.overallScore))}>
                            {candidate.overallScore.toFixed(1)}
                          </span>
                          <p className="text-xs text-slate-500">Fit Score</p>
                        </div>
                      </div>
                    </div>

                    {/* Verdict */}
                    <div className="flex items-center gap-2">
                      <Badge className={verdictDisplay.color}>
                        {verdictDisplay.icon}
                        <span className="ml-1">{verdictDisplay.label}</span>
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {candidate.analysisDate.toLocaleDateString()}
                      </span>
                    </div>

                    {/* Key Insights */}
                    <div className="space-y-3">
                      {/* Skills Match */}
                      {candidate.matchedSkills.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            Matched Skills ({candidate.matchedSkills.length})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {candidate.matchedSkills.slice(0, 4).map(skill => (
                              <Badge key={skill} variant="outline" className="text-xs bg-green-50 border-green-200 text-green-800">
                                {skill}
                              </Badge>
                            ))}
                            {candidate.matchedSkills.length > 4 && (
                              <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200 text-slate-600">
                                +{candidate.matchedSkills.length - 4} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Key Strengths */}
                      {candidate.strengths.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                            <Star className="h-4 w-4 text-blue-600" />
                            Key Strengths
                          </p>
                          <ul className="text-sm text-slate-700 space-y-1">
                            {candidate.strengths.slice(0, 2).map((strength, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <div className="w-1 h-1 bg-slate-400 rounded-full mt-2 flex-shrink-0"></div>
                                <span className="line-clamp-1">{strength}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Areas for Improvement */}
                      {candidate.weaknesses.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            Areas to Consider
                          </p>
                          <ul className="text-sm text-slate-600 space-y-1">
                            {candidate.weaknesses.slice(0, 1).map((weakness, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <div className="w-1 h-1 bg-slate-400 rounded-full mt-2 flex-shrink-0"></div>
                                <span className="line-clamp-1">{weakness}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>T: {candidate.technicalScore.toFixed(1)}</span>
                        <span>•</span>
                        <span>E: {candidate.experienceScore.toFixed(1)}</span>
                        <span>•</span>
                        <span>C: {candidate.culturalFitScore.toFixed(1)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {projectId && onAddToProject && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => onAddToProject(candidate)}
                            className="h-8 px-3 text-xs rounded-xl"
                          >
                            <UserPlus className="h-3 w-3 mr-1" />
                            Add
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => onCandidateSelect?.(candidate)}
                          className="h-8 px-3 text-xs rounded-xl group-hover:bg-slate-900 group-hover:text-white transition-colors"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredResults.length === 0 && (
          <Card className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/60">
            <CardContent className="py-12">
              <div className="text-center">
                <Search className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No candidates found</h3>
                <p className="text-slate-500">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}